/**
 * usePatientQuickAdd — Shared hook for quick patient search & creation
 *
 * Extracts the core search + quick-add logic from PatientModal so it can be
 * reused by the patient modal and the Activity Bar card.
 *
 * See: docs/features/infrastructure/app-shell.md#quick-patient-add
 */
import { useState, useMemo, useCallback, useRef } from "react";
import { createQuickPatient, patchPatientDirectory } from "../utils/patientDirectoryService";
import { useAuth } from "../utils/authContext";
import { usePatientDirectory } from "./usePatientDirectory";

/**
 * Simple fuzzy match - checks if all characters appear in order
 */
const fuzzyMatch = (text, query) => {
  if (!query) return true;
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  let queryIndex = 0;
  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      queryIndex++;
    }
  }
  return queryIndex === queryLower.length;
};

/**
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to fetch patients (default: true)
 * @param {Function} options.onPatientSelect - Callback when patient is selected/created
 * @returns {Object} Hook API
 */
export function usePatientQuickAdd({ enabled = true, onPatientSelect } = {}) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // State
  // Reuse the identity-scoped directory, including active shared patients and
  // their access metadata, instead of fetching only the user's owned IDs.
  const { patients, isLoading, refresh } = usePatientDirectory(enabled ? userId : null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const pendingCreation = useRef(null);

  const creationIdFor = useCallback((first, last) => {
    if (pendingCreation.current?.first !== first || pendingCreation.current?.last !== last) {
      pendingCreation.current = { first, last, id: crypto.randomUUID() };
    }
    return pendingCreation.current.id;
  }, []);

  const fetchPatients = useCallback(() => refresh().catch(error => {
    console.error("[usePatientQuickAdd] Error fetching patients:", error);
  }), [refresh]);

  // Reset state
  const reset = useCallback(() => {
    pendingCreation.current = null;
    setSearchQuery("");
    setShowAddForm(false);
    setFirstName("");
    setLastName("");
  }, []);

  // Handle clicking "Add new" — parse search query into name fields
  const handleAddNewClick = useCallback(() => {
    setShowAddForm(true);
    const words = searchQuery.trim().split(/\s+/);
    if (words.length >= 2) {
      // Two or more words: first word is first name, rest is last name
      setFirstName(words[0]);
      setLastName(words.slice(1).join(" "));
    } else {
      // Single word: put in last name field
      setLastName(searchQuery.trim());
      setFirstName("");
    }
  }, [searchQuery]);

  // Swap first and last name
  const handleSwapNames = useCallback(() => {
    setFirstName((prev) => {
      const temp = lastName;
      setLastName(prev);
      return temp;
    });
  }, [lastName]);

  // Select existing patient
  const selectPatient = useCallback(
    (patient) => {
      onPatientSelect?.(patient);
    },
    [onPatientSelect]
  );

  // Create new patient
  const createPatient = useCallback(async () => {
    if (isCreating) return null;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    // Need at least one name with 2+ characters
    if (trimmedFirst.length < 2 && trimmedLast.length < 2) {
      return null;
    }

    setIsCreating(true);
    try {
      const newPatient = await createQuickPatient(trimmedFirst, trimmedLast, creationIdFor(trimmedFirst, trimmedLast));
      pendingCreation.current = null;
      patchPatientDirectory(userId, newPatient.id, newPatient);

      // Dispatch event for other listeners
      window.dispatchEvent(
        new CustomEvent("patientCreated", {
          detail: { patientData: newPatient },
        })
      );

      // Select the new patient
      onPatientSelect?.(newPatient);

      return newPatient;
    } catch (error) {
      console.error("[usePatientQuickAdd] Error creating patient:", error);
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [firstName, lastName, isCreating, onPatientSelect, creationIdFor, userId]);

  // Filter patients with fuzzy matching on full name
  const filteredPatients = useMemo(() => {
    if (!searchQuery.trim()) {
      return [...patients].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
    }

    return patients
      .filter((patient) => {
        const fullName = `${patient.firstName || ""} ${patient.lastName || ""}`;
        const reverseName = `${patient.lastName || ""} ${patient.firstName || ""}`;
        return fuzzyMatch(fullName, searchQuery) || fuzzyMatch(reverseName, searchQuery);
      })
      .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
  }, [patients, searchQuery]);

  // Check if the search query contains two or more words (first + last)
  const searchWords = searchQuery.trim().split(/\s+/);
  const hasTwoWords = searchWords.length >= 2;

  // Quick-create: for two-word queries, set names and create immediately
  const quickCreatePatient = useCallback(async () => {
    if (isCreating) return null;
    const words = searchQuery.trim().split(/\s+/);
    if (words.length < 2) return null;

    const first = words[0];
    const last = words.slice(1).join(" ");
    setFirstName(first);
    setLastName(last);

    // Create directly — can't rely on state update for firstName/lastName,
    // so build patient data inline
    const trimmedFirst = first.trim();
    const trimmedLast = last.trim();
    if (trimmedFirst.length < 2 && trimmedLast.length < 2) return null;

    setIsCreating(true);
    try {
      const newPatient = await createQuickPatient(trimmedFirst, trimmedLast, creationIdFor(trimmedFirst, trimmedLast));
      pendingCreation.current = null;
      patchPatientDirectory(userId, newPatient.id, newPatient);
      window.dispatchEvent(
        new CustomEvent("patientCreated", { detail: { patientData: newPatient } })
      );
      onPatientSelect?.(newPatient);
      return newPatient;
    } catch (error) {
      console.error("[usePatientQuickAdd] Error quick-creating patient:", error);
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [searchQuery, isCreating, onPatientSelect, creationIdFor, userId]);

  // Computed flags
  const canShowAddNew = searchQuery.trim().length >= 2;
  const canCreate = firstName.trim().length >= 2 || lastName.trim().length >= 2;

  return {
    // State
    patients,
    filteredPatients,
    isLoading,
    searchQuery,
    setSearchQuery,
    showAddForm,
    setShowAddForm,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isCreating,

    // Computed
    canShowAddNew,
    canCreate,
    hasTwoWords,

    // Actions
    reset,
    fetchPatients,
    handleAddNewClick,
    handleSwapNames,
    selectPatient,
    createPatient,
    quickCreatePatient,
  };
}
